<script setup>
import { ref, computed, getCurrentInstance, watch, inject, onMounted } from 'vue';
import logoUrl from '@/assets/login-logo.png';
import AuthLayout from "@/layout/auth/AuthLayout.vue";
import { Head, Link, useForm } from '@inertiajs/vue3';
import GlobalLoadingOverlay from '@/components/GlobalLoadingOverlay.vue';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    first_name: '',
    last_name: '',
    name: '',
    birthday: '',
    phone: '',
    email: '',
    email_confirmation: '',
    password: '',
    password_confirmation: '',
    locale: 'en',
    coin: 'USD',
    notify_updates_via_email: 'MINIMAL',
    introduced_by: '',
});

const authLocale = inject('authLocale', null);

if (authLocale) {
    onMounted(() => {
        form.locale = authLocale.locale.value;
    });
    
    watch(() => authLocale.locale.value, (newLocale) => {
        if (newLocale !== form.locale) {
            form.locale = newLocale;
        }
    });
    
    watch(() => form.locale, (newLocale) => {
        if (newLocale !== authLocale.locale.value) {
            authLocale.updateLocale(newLocale);
        }
    });
}

const { showPageLoading, hidePageLoading } = useEnhancedLoading();

// Password confirmation validation
const passwordError = ref('');

const emailError = ref('');

watch([() => form.password, () => form.password_confirmation], ([password, confirmation]) => {
    if (confirmation && password !== confirmation) {
        // TODO how to show error message
        passwordError.value = 'Passwords do not match';
    } else {
        passwordError.value = '';
    }
});

watch([() => form.email, () => form.email_confirmation], ([email, confirmation]) => {
    if (confirmation && email !== confirmation) {
        emailError.value = 'Emails do not match';
    } else {
        emailError.value = '';
    }
    if (form.errors.email) {
        form.clearErrors('email');
    }
});

const submit = () => {
    if (form.email !== form.email_confirmation) {
        emailError.value = 'Emails do not match';
        return;
    }

    // Check password confirmation before submitting
    if (form.password !== form.password_confirmation) {
        passwordError.value = 'Passwords do not match';
        return;
    }

    form.transform(data => ({
        ...data,
    })).post(route('client.register'), {
        onStart: () => {
            showPageLoading($t('Creating account...'));
        },
        onFinish: () => {
            form.reset('password', 'password_confirmation');
            hidePageLoading();
        },
        onError: () => {
            hidePageLoading();
        }
    });
};

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const supportMenu = ref();
const supportItems = computed(() => ([
    {
        label: $t('Whatsapp'),
        icon: 'pi pi-whatsapp',
        url: 'https://wa.me/5547989104747?text=Ol%C3%A1%21+Preciso+de+ajuda+para+logar+no+sistema',
        target: '_blank'
    },
    {
        label: $t('Telegram'),
        icon: 'pi pi-telegram',
        url: 'https://t.me/maxpiress',
        target: '_blank'
    },
    {
        label: $t('Skype'),
        icon: 'pi pi-skype',
        url: 'https://msng.link/o/?maxpires=sk',
        target: '_blank'
    }
]));

const localeOptions = ref([
    { name: 'English', code: 'en' },
    { name: 'Espanhol', code: 'es' },
    { name: 'Português', code: 'pt' }
]);

const coinOptions = ref([
    { name: 'USD', code: 'USD' },
    { name: 'EUR', code: 'EUR' },
    { name: 'BRL', code: 'BRL' },
    { name: 'GBP', code: 'GBP' }
]);

const emailNotificationOptions = ref([
    { name: 'Disabled', code: 'DISABLED' },
    { name: 'Minimal', code: 'MINIMAL' },
    { name: 'All', code: 'ALL' }
]);

defineOptions({
    layout: AuthLayout,
});
</script>

<template>
    <GlobalLoadingOverlay />
    <Head :title="$t('Register')" />

    <div class="border mx-2 sm:mx-auto px-5 sm:px-20 pb-16 rounded-2xl">
        <h1 class="text-4xl sm:text-5xl font-medium text-center mx-auto leading-[125%] my-12 sm:min-w-96">
            <img :src="logoUrl" class="h-16 w-auto mx-auto" alt="Logo"/>
        </h1>

        <form @submit.prevent="submit" class="max-w-[35rem] mx-auto space-y-6">
            <div v-if="status" class="font-medium text-sm text-green-600 mb-4">
                {{ status }}
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <FloatLabel variant="on">
                        <InputText
                            v-model="form.first_name"
                            type="text"
                            :class="{ 'p-invalid': form.errors.first_name }"
                            required
                        />
                        <label for="locale" class="text-sm text-surface-500">{{ $t('First name') }} <span class="required-color">*</span></label>
                    </FloatLabel>
                    <small v-if="form.errors.first_name" class="p-error">{{ form.errors.first_name }}</small>
                </div>
                <div class="flex flex-col gap-2">
                    <FloatLabel variant="on">
                        <InputText
                            v-model="form.last_name"
                            type="text"
                            :class="{ 'p-invalid': form.errors.last_name }"
                            required
                        />
                        <label for="locale" class="text-sm text-surface-500">{{ $t('Last name') }} <span class="required-color">*</span></label>
                    </FloatLabel>

                    <small v-if="form.errors.last_name" class="p-error">{{ form.errors.last_name }}</small>
                </div>
            </div>

            <div class="flex flex-col w-full gap-2">
                <FloatLabel variant="on">
                    <InputText
                        v-model="form.email"
                        type="email"
                        :class="{ 'p-invalid': form.errors.email }"
                        class="w-full"
                        required
                    />
                    <label for="locale" class="text-sm text-surface-500">{{ $t('Email') }} <span class="required-color">*</span></label>
                </FloatLabel>
                <small v-if="form.errors.email" class="p-error">{{ $t(form.errors.email) }}</small>
            </div>

            <div class="flex flex-col gap-2">
                <FloatLabel variant="on">
                    <InputText
                        v-model="form.email_confirmation"
                        type="email"
                        :class="{ 'p-invalid': form.errors.email_confirmation || emailError }"
                        class="w-full"
                        required
                    />
                    <label for="locale" class="text-sm text-surface-500">{{ $t('Confirm email') }} <span class="required-color">*</span></label>
                </FloatLabel>
                <small v-if="form.errors.email_confirmation" class="p-error">{{ form.errors.email_confirmation }}</small>
                <small v-if="emailError && !form.errors.email_confirmation" class="p-error">{{ emailError }}</small>
            </div>


            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <FloatLabel variant="on">
                        <Password
                            v-model="form.password"
                            :feedback="false"
                            :class="{ 'p-invalid': form.errors.password }"
                            required
                        />
                        <label for="locale" class="text-sm text-surface-500">{{ $t('Password') }} <span class="required-color">*</span></label>
                    </FloatLabel>
                    <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
                </div>
                <div class="flex flex-col gap-2">
                    <FloatLabel variant="on">
                        <Password
                            v-model="form.password_confirmation"
                            :feedback="false"
                            :class="{ 'p-invalid': form.errors.password_confirmation || passwordError }"
                            required
                        />
                        <label for="locale" class="text-sm text-surface-500">{{ $t('Confirm password') }} <span class="required-color">*</span></label>
                    </FloatLabel>
                    <small v-if="form.errors.password_confirmation" class="p-error">{{ form.errors.password_confirmation }}</small>
                    <small v-if="passwordError && !form.errors.password_confirmation" class="p-error">{{ passwordError }}</small>
                </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <FloatLabel variant="on">
                        <Select
                            id="locale"
                            v-model="form.locale"
                            :options="localeOptions"
                            optionLabel="name"
                            optionValue="code"
                            class="w-full h-[45px] items-center"
                            :class="{ 'p-invalid': form.errors.locale }"
                        />
                        <label for="locale" class="text-sm text-surface-500">{{ $t('Locale') }} <span class="required-color">*</span></label>
                    </FloatLabel>
                    <small v-if="form.errors.locale" class="p-error">{{ form.errors.locale }}</small>
                </div>
                <div class="flex flex-col gap-2">
                    <FloatLabel variant="on">
                        <Select
                            id="coin"
                            v-model="form.coin"
                            :options="coinOptions"
                            optionLabel="name"
                            optionValue="code"
                            :placeholder="$t('Select Coin')"
                            class="w-full h-[45px] items-center"
                            :class="{ 'p-invalid': form.errors.coin }"
                        />
                        <label for="coin" class="text-sm text-surface-500">{{ $t('Coin') }} <span class="required-color">*</span></label>
                    </FloatLabel>
                    <small v-if="form.errors.coin" class="p-error">{{ form.errors.coin }}</small>
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <FloatLabel variant="on">
                    <Select
                        id="notifications"
                        v-model="form.notify_updates_via_email"
                        :options="emailNotificationOptions"
                        optionLabel="name"
                        optionValue="code"
                        class="w-full h-[45px] items-center"
                        :class="{ 'p-invalid': form.errors.notify_updates_via_email }"
                    />
                    <label for="notifications" class="text-sm text-surface-500">{{ $t('E-mail notifications') }} <span class="required-color">*</span></label>
                </FloatLabel>

                <small v-if="form.errors.notify_updates_via_email" class="p-error">{{ form.errors.notify_updates_via_email }}</small>
            </div>

            <div class="flex flex-col gap-2">
                <FloatLabel variant="on">
                    <InputText
                        id="introduced_by"
                        v-model="form.introduced_by"
                        type="text"
                        class="w-full"
                        :class="{ 'p-invalid': form.errors.introduced_by }"
                    />
                    <label for="introduced_by" class="text-sm text-surface-500">{{ $t('Were you referred by someone?') }}</label>
                </FloatLabel>
                <small v-if="form.errors.introduced_by" class="p-error">{{ form.errors.introduced_by }}</small>
            </div>

            <div class="space-y-8 mt-8 text-sm">
                <div class="flex items-center justify-between">
                    <div class="text-surface-500 text-center leading-6">
                        {{ $t('Already registered?') }}
                        <Link
                            :href="route('client.login')"
                            class="text-primary-active font-medium hover:opacity-75 transition-all"
                        >
                            {{ $t('Login') }}
                        </Link>
                    </div>
                </div>

                <Button
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    type="submit"
                    class="landing-button-primary w-full"
                >
                    {{ $t('Register') }}
                </Button>
            </div>
        </form>

        <!-- Contact Support Section -->
        <div class="mt-6 max-w-[35rem] mx-auto">
            <div class="relative">
                <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-300" />
                </div>
                <div class="relative flex justify-center text-sm">
                    <span class="bg-white px-2 text-gray-500">{{ $t('Need some help?') }}</span>
                </div>
            </div>

            <div class="mt-6">
                <div class="relative">
                    <button type="button"
                            class="inline-flex w-full justify-center items-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-500 shadow-sm hover:bg-gray-50"
                            @click="(e) => supportMenu && supportMenu.toggle(e)">
                        <span>{{ $t('Our team is here') }}</span>
                        <i class="pi pi-chevron-down ml-3"></i>
                    </button>
                    <Menu ref="supportMenu" :model="supportItems" :popup="true" class="w-48" />
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.p-error {
    color: red;
}
.required-color {
    color: red;
}
</style>
